import { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, LoadingIndicator } from '@cfa/react-components';
import { Section, SectionHeader, OrderSummary, Totals, EventSummary } from '../components';
import { selectGuestDetails, selectIsGuestUser, actions as guestActions } from '../reducers/guest';
import { actions as paymentActions } from '../reducers/payment';
import {
  selectDestination,
  selectEventDetails,
  selectCartItems,
  selectDestinationDetails,
  selectPaymentSubmitted,
  actions as orderActions,
  selectFormattedTotals,
  selectDeliveryTipIsDisplayed,
  selectDeliveryTipLoading,
  selectOrderIsSubmitStatus,
} from '../reducers/order';
import { selectAppLoading } from '../reducers';
import { getHash } from '../util/location';
import { logAmplitudeEvent } from '../services/amplitude';

export class IntroSummary extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    const {
      guestDetails,
      destination,
      eventDetails,
      destinationDetails,
      formattedTotals,
      cartItems,
      isGuestUser,
      loading,
      stepCompleted,
      deliveryTipIsDisplayed,
      deliveryTipLoading,
      orderIsSubmitStatus,
      throwAlreadySubmittedFatalError,
    } = this.props;

    if (!loading && orderIsSubmitStatus && !deliveryTipIsDisplayed) {
      throwAlreadySubmittedFatalError();
    }
    logAmplitudeEvent('[My Payment] New Payment Intro Summary Rendered', {
      guestDetails,
      destination,
      eventDetails,
    });

    const renderButton = () => {
      const handleContinueClick = () => {
        if (isGuestUser) {
          logAmplitudeEvent('[My Payment] Checkout as Guest');
        }
        stepCompleted(0);
      };
      return (
        <div className="continue">
          <NavLink to={`/payment/#id_token=${getHash().id_token}`} data-testid="nav-link">
            <Button className="button-text" onClick={handleContinueClick}>
              Continue to Payment
            </Button>
          </NavLink>
        </div>
      );
    };

    return (
      <StyledIntroSummary>
        <Section>
          {loading ? (
            <LoadingIndicator variant="page" size="lg" className="loading" data-testid="loading-indicator" />
          ) : (
            <>
              <SectionHeader>Order summary</SectionHeader>
              <EventSummary
                destination={destination}
                guestDetails={guestDetails}
                eventDetails={eventDetails}
                destinationDetails={destinationDetails}
                orderIsSubmitStatus={orderIsSubmitStatus}
              />
              <OrderSummary cartItems={cartItems} />
              <Totals
                subTotal={formattedTotals.subTotal}
                tax={formattedTotals.tax}
                taxAndTotal={formattedTotals.taxAndTotal}
                deliveryTipIsDisplayed={deliveryTipIsDisplayed}
                deliveryTipAmount={formattedTotals.deliveryTipAmount}
                deliveryTipLoading={deliveryTipLoading}
              />
              {renderButton()}
            </>
          )}
        </Section>
      </StyledIntroSummary>
    );
  }
}

const StyledIntroSummary = styled.div`
  & .continue {
    display: flex;
    justify-content: flex-end;
  }
  & .loading {
    margin: 0 auto;
  }
  @media (max-width: 500px) {
    & .continue button,
    .continue a {
      width: 100%;
    }
  }
`;

IntroSummary.propTypes = {
  destination: PropTypes.string.isRequired,
  guestDetails: PropTypes.objectOf(PropTypes.string),
  eventDetails: PropTypes.shape({
    time: PropTypes.string,
    guestCount: PropTypes.number,
    paperGoods: PropTypes.bool,
    specialInstructions: PropTypes.string,
    date: PropTypes.object,
  }),
  destinationDetails: PropTypes.objectOf(PropTypes.string),
  isGuestUser: PropTypes.bool,
  cartItems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  match: PropTypes.shape({
    path: PropTypes.string,
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.object,
  }).isRequired,
  stepCompleted: PropTypes.func.isRequired,
  formattedTotals: PropTypes.objectOf(PropTypes.string),
  deliveryTipIsDisplayed: PropTypes.bool,
  deliveryTipLoading: PropTypes.bool,
  orderIsSubmitStatus: PropTypes.bool,
  throwAlreadySubmittedFatalError: PropTypes.func,
};

IntroSummary.defaultProps = {
  guestDetails: {},
  destinationDetails: {},
  eventDetails: {},
  cartItems: [{}],
  formattedTotals: {},
  loading: false,
  deliveryTipIsDisplayed: false,
  deliveryTipLoading: false,
  orderIsSubmitStatus: false,
  throwAlreadySubmittedFatalError: () => {},
  isGuestUser: false,
};

/* v8 ignore start */
function mapStateToProps(state) {
  return {
    guestDetails: selectGuestDetails(state),
    eventDetails: selectEventDetails(state),
    isGuestUser: selectIsGuestUser(state),
    destination: selectDestination(state),
    loading: selectAppLoading(state),
    cartItems: selectCartItems(state),
    destinationDetails: selectDestinationDetails(state),
    submitted: selectPaymentSubmitted(state),
    formattedTotals: selectFormattedTotals(state),
    deliveryTipIsDisplayed: selectDeliveryTipIsDisplayed(state),
    deliveryTipLoading: selectDeliveryTipLoading(state),
    orderIsSubmitStatus: selectOrderIsSubmitStatus(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...guestActions, ...paymentActions, ...orderActions }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(IntroSummary);
